import { createFileRoute, useRouter } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AUTH_ERROR_MESSAGES, authErrorMessage } from "@/lib/authErrors";
import { supabase } from "@/lib/supabase";

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const router = useRouter();
  const { t } = useTranslation("consumer");
  const [ready, setReady] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;

    const applySession = (session: { user: unknown } | null) => {
      if (!mounted) return;
      setHasSession(!!session?.user);
      setReady(true);
    };

    void supabase.auth.getSession().then(({ data: { session } }) => {
      applySession(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN" || event === "INITIAL_SESSION") {
        applySession(session);
      }
    });

    const timeoutId = window.setTimeout(() => {
      if (mounted) setReady(true);
    }, 2500);

    return () => {
      mounted = false;
      subscription.unsubscribe();
      window.clearTimeout(timeoutId);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError(authErrorMessage("passwordTooShort"));
      return;
    }
    if (password !== confirm) {
      setError(authErrorMessage("passwordMismatch"));
      return;
    }

    setSaving(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      setSuccess(true);
      window.setTimeout(() => {
        void router.navigate({ to: "/" });
      }, 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : authErrorMessage("passwordUpdateFailed"));
    } finally {
      setSaving(false);
    }
  };

  if (!ready) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center px-5">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!hasSession) {
    return (
      <div className="px-5 py-16">
        <h1 className="text-2xl font-bold">{t("login.resetPasswordTitle")}</h1>
        <p className="mt-3 text-sm text-muted-foreground">{AUTH_ERROR_MESSAGES.recoveryLinkInvalid}</p>
      </div>
    );
  }

  return (
    <div className="px-5 py-16">
      <h1 className="text-2xl font-bold">{t("login.setNewPasswordTitle")}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{t("login.setNewPasswordHint")}</p>

      {success ? (
        <p className="mt-6 rounded-2xl border border-pulse/30 bg-pulse/10 px-4 py-3 text-sm text-pulse">
          {AUTH_ERROR_MESSAGES.passwordUpdated} {t("login.redirecting")}
        </p>
      ) : (
        <form onSubmit={(e) => void handleSubmit(e)} className="mt-6 space-y-4">
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t("login.newPassword")}
            </span>
            <input
              type="password"
              required
              minLength={6}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full rounded-2xl border border-border bg-surface px-4 py-3.5 text-sm outline-none focus:border-pulse"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t("login.confirmPassword")}
            </span>
            <input
              type="password"
              required
              minLength={6}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-2 w-full rounded-2xl border border-border bg-surface px-4 py-3.5 text-sm outline-none focus:border-pulse"
            />
          </label>

          {error && (
            <p className="rounded-2xl border border-pulse/30 bg-pulse/10 px-4 py-3 text-sm text-pulse">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={saving}
            className="flex w-full items-center justify-center rounded-2xl bg-gradient-pulse py-3.5 text-sm font-bold text-pulse-foreground shadow-pulse disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : t("login.savePassword")}
          </button>
        </form>
      )}
    </div>
  );
}
